/* LPC product card behaviour (snippets/lpc-card.liquid).
   - colour dot: swap the card image, point every link at ?variant=<id>
   - "+N": reveal the remaining dots
   - quick add: one shared right-hand <dialog> ("Choose your size"), created on first use
   Plain document-level listeners, so it works for cards added later and is safe to include twice.
   Text can be overridden with window.lpcCardStrings = { chooseSize: '...' } before this file runs. */
(function () {
  'use strict';

  if (window.lpcCardLoaded) return;
  window.lpcCardLoaded = true;

  const T = Object.assign(
    {
      sheetTitle: 'Choose your size',
      quickTitle: 'Quick add',
      close: 'Close',
      loading: 'Loading options…',
      loadError: 'Couldn’t load these options. Please open the product to continue.',
      addError: 'Couldn’t add this item. Please try again.',
      chooseSize: 'Please choose a size.',
      soldOut: 'Sold out',
      adding: 'Adding…',
      addToCart: (window.variantStrings && window.variantStrings.addToCart) || 'Add to cart',
      viewProduct: 'View full details',
      openProduct: 'Open product',
      color: 'Color',
      size: 'Size',
      from: 'From',
      regularPrice: 'Regular price',
    },
    window.lpcCardStrings || {}
  );

  const IMAGE_WIDTHS = [180, 300, 450, 700];
  const ICON_CLOSE =
    '<svg class="lpc-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18"/></svg>';

  /* ---------- small helpers ---------- */

  function el(tag, attrs, kids) {
    const node = document.createElement(tag);
    Object.keys(attrs || {}).forEach((key) => {
      const value = attrs[key];
      if (value === false || value == null) return;
      if (key === 'text') node.textContent = value;
      else node.setAttribute(key, value === true ? '' : value);
    });
    (kids || []).forEach((kid) => {
      if (kid) node.appendChild(typeof kid === 'string' ? document.createTextNode(kid) : kid);
    });
    return node;
  }

  function money(cents) {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency,
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  /* Shopify image URLs take ?width=. Our card images always carry one. */
  function withWidth(url, width) {
    return url.replace(/([?&])width=\d+/, '$1width=' + width);
  }

  function setImage(img, url) {
    if (!/[?&]width=\d+/.test(url)) {
      img.removeAttribute('srcset');
      img.src = url;
      return;
    }
    img.srcset = IMAGE_WIDTHS.map((w) => withWidth(url, w) + ' ' + w + 'w').join(', ');
    img.src = withWidth(url, 700);
  }

  /* /products/handle -> /products/handle?variant=123 (keeps any query already on the url) */
  function withVariant(url, id) {
    const next = new URL(url, window.location.origin);
    next.searchParams.set('variant', id);
    return next.pathname + next.search;
  }

  /* ---------- card: colour dots and "+N" ---------- */

  function pickDot(dot) {
    const card = dot.closest('[data-lpc-card]');
    if (!card) return;
    card.querySelectorAll('[data-lpc-dot]').forEach((other) => {
      other.setAttribute('aria-pressed', String(other === dot));
    });
    const img = card.querySelector('[data-lpc-img]');
    if (img && dot.dataset.image) setImage(img, dot.dataset.image);
    const href = withVariant(card.dataset.url, dot.dataset.variant);
    card.querySelectorAll('[data-lpc-link]').forEach((link) => link.setAttribute('href', href));
  }

  function showAllDots(button) {
    const extras = button.closest('.lpc-card__colors').querySelectorAll('[data-lpc-extra]');
    extras.forEach((dot) => dot.removeAttribute('hidden'));
    button.remove();
    if (extras[0]) extras[0].focus();
  }

  /* ---------- quick-add sheet ---------- */

  const productCache = {};
  let sheet = null;

  function getSheet() {
    if (sheet) return sheet;

    const title = el('h2', { class: 'lpc-heading lpc-card-sheet__title', id: 'LpcCardSheetTitle' });
    const closeButton = el('button', {
      type: 'button',
      class: 'lpc-card-sheet__close',
      'aria-label': T.close,
    });
    closeButton.innerHTML = ICON_CLOSE;
    const body = el('div', { class: 'lpc-card-sheet__body' });
    const dialog = el('dialog', { class: 'lpc-card-sheet', 'aria-labelledby': 'LpcCardSheetTitle' }, [
      el('div', { class: 'lpc-card-sheet__head' }, [title, closeButton]),
      body,
    ]);
    document.body.appendChild(dialog);

    sheet = { dialog: dialog, title: title, body: body, opener: null, token: 0 };

    closeButton.addEventListener('click', () => dialog.close());

    /* Backdrop click closes, but not when a drag that started inside ends outside. */
    let pressedBackdrop = false;
    dialog.addEventListener('pointerdown', (event) => {
      pressedBackdrop = event.target === dialog;
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog && pressedBackdrop) dialog.close();
    });

    dialog.addEventListener('close', () => {
      sheet.token += 1; // ignore any request still in flight
      document.documentElement.classList.remove('lpc-card-lock');
    });

    return sheet;
  }

  function showNote(text, href) {
    sheet.body.replaceChildren(
      el('p', { class: 'lpc-card-sheet__note', role: 'status', text: text }),
      href ? el('a', { class: 'lpc-btn', href: href, text: T.openProduct }) : null
    );
  }

  function openQuickAdd(link) {
    const card = link.closest('[data-lpc-card]');
    if (!card) return;

    const s = getSheet();
    const token = (s.token += 1);
    s.opener = link;
    s.title.textContent = T.sheetTitle;
    showNote(T.loading);
    if (!s.dialog.open) {
      s.dialog.showModal();
      document.documentElement.classList.add('lpc-card-lock');
    }

    const url = card.dataset.jsonUrl;
    if (!productCache[url]) {
      productCache[url] = fetch(url, { headers: { Accept: 'application/json' } }).then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.json();
      });
    }
    productCache[url]
      .then((product) => {
        if (token === s.token) renderProduct(s, product, card);
      })
      .catch(() => {
        delete productCache[url];
        if (token === s.token) showNote(T.loadError, card.dataset.url);
      });
  }

  function renderProduct(s, product, card) {
    const optionNames = product.options.map((option) => (typeof option === 'string' ? option : option.name));
    const colorIdx = optionNames.findIndex((name) => /^colou?r$/i.test(String(name).trim()));
    const others = optionNames.map((name, i) => i).filter((i) => i !== colorIdx);
    if (others.length > 1) {
      showNote(T.loadError, card.dataset.url); // more than colour + one other option: let the product page handle it
      return;
    }

    const optionOf = (variant, idx) => (variant.options ? variant.options[idx] : variant['option' + (idx + 1)]);
    const valuesOf = (idx) => {
      const option = product.options[idx];
      if (option && option.values) return option.values;
      return product.variants.reduce((list, v) => {
        const value = optionOf(v, idx);
        return list.indexOf(value) > -1 ? list : list.concat(value);
      }, []);
    };

    let sizeIdx = others.length ? others[0] : -1;
    let sizes = sizeIdx > -1 ? valuesOf(sizeIdx) : [];
    if (sizes.length === 1 && sizes[0] === 'Default Title') {
      sizeIdx = -1;
      sizes = [];
    }
    const colors = colorIdx > -1 ? valuesOf(colorIdx) : [];

    const pick = (color, size) =>
      product.variants.find(
        (v) => (colorIdx < 0 || optionOf(v, colorIdx) === color) && (sizeIdx < 0 || optionOf(v, sizeIdx) === size)
      );
    const ofColor = (color) => product.variants.filter((v) => colorIdx < 0 || optionOf(v, colorIdx) === color);

    /* Start on the colour the card is showing (its pressed dot), else the first one in stock. */
    const pressed = card.querySelector('[data-lpc-dot][aria-pressed="true"]');
    const start =
      product.variants.find((v) => pressed && String(v.id) === pressed.dataset.variant) ||
      product.variants.find((v) => v.available) ||
      product.variants[0];
    const state = {
      color: colorIdx > -1 ? optionOf(start, colorIdx) : null,
      size: sizes.length === 1 ? sizes[0] : null,
    };

    /* Dot colours are read from the card, because /products/<handle>.js has no swatch data. */
    const swatchOf = {};
    card.querySelectorAll('[data-lpc-dot]').forEach((dot) => {
      swatchOf[dot.getAttribute('aria-label')] = dot.style.getPropertyValue('--lpc-dot');
    });

    s.title.textContent = sizes.length ? T.sheetTitle : T.quickTitle;

    /* --- header: picture, title, price --- */
    const thumb = el('img', { class: 'lpc-card-sheet__thumb', alt: '', width: 84, height: 105 });
    const nameLink = el('a', { class: 'lpc-card-sheet__name', href: card.dataset.url, text: product.title });
    const priceLine = el('p', { class: 'lpc-card-sheet__price' });
    const header = el('div', { class: 'lpc-card-sheet__product' }, [thumb, el('div', null, [nameLink, priceLine])]);

    /* --- colour dots --- */
    const colorName = el('strong');
    let colorGroup = null;
    if (colors.length > 1) {
      const swatches = colors.map((color) => {
        const input = el('input', { type: 'radio', name: 'lpc-quick-color', value: color, checked: color === state.color });
        const fill = el('span', { class: 'lpc-card-sheet__swatch-fill' });
        if (swatchOf[color]) fill.style.setProperty('--lpc-dot', swatchOf[color]);
        return el('label', { class: 'lpc-card-sheet__swatch', title: color }, [input, fill]);
      });
      colorGroup = el('fieldset', { class: 'lpc-card-sheet__group' }, [
        el('legend', { class: 'lpc-card-sheet__legend' }, [T.color + ': ', colorName]),
        el('div', { class: 'lpc-card-sheet__dots' }, swatches),
      ]);
    }

    /* --- size buttons --- */
    let sizeGroup = null;
    const sizeInputs = {};
    if (sizes.length > 1) {
      const buttons = sizes.map((size) => {
        sizeInputs[size] = el('input', { type: 'radio', name: 'lpc-quick-size', value: size });
        return el('label', { class: 'lpc-card-sheet__size' }, [
          sizeInputs[size],
          el('span', { class: 'lpc-card-sheet__size-label', text: size }),
        ]);
      });
      sizeGroup = el('fieldset', { class: 'lpc-card-sheet__group' }, [
        el('legend', { class: 'lpc-card-sheet__legend', text: T.size }),
        el('div', { class: 'lpc-card-sheet__sizes' }, buttons),
      ]);
    }

    const status = el('p', { class: 'lpc-card-sheet__status', role: 'status' });
    const addButton = el('button', { type: 'submit', class: 'lpc-btn lpc-btn--buy lpc-card-sheet__add', text: T.addToCart });
    const detailsLink = el('a', { class: 'lpc-card-sheet__link', href: card.dataset.url, text: T.viewProduct });
    const form = el('form', { class: 'lpc-card-sheet__form', novalidate: true }, [
      colorGroup,
      sizeGroup,
      status,
      addButton,
    ]);
    s.body.replaceChildren(header, form, detailsLink);

    const say = (text, isError) => {
      status.textContent = text || '';
      status.classList.toggle('is-error', Boolean(isError));
    };

    /* Everything on screen follows `state`. */
    const refresh = () => {
      colorName.textContent = state.color || '';

      sizes.forEach((size) => {
        const input = sizeInputs[size];
        if (!input) return;
        const variant = pick(state.color, size);
        input.disabled = !variant || !variant.available;
        if (state.size === size && input.disabled) state.size = null;
        input.checked = state.size === size;
      });

      const chosen = pick(state.color, state.size);
      const forColor = ofColor(state.color);
      const shown = chosen || forColor[0];
      const picture = shown && ((shown.featured_image && shown.featured_image.src) || product.featured_image);
      if (picture) {
        const next = new URL(picture, window.location.origin);
        next.searchParams.set('width', 168);
        if (thumb.getAttribute('src') !== next.toString()) thumb.src = next.toString();
      }

      const pool = chosen ? [chosen] : forColor;
      const prices = pool.map((v) => v.price);
      const low = Math.min.apply(null, prices);
      const compares = pool.map((v) => v.compare_at_price || 0).filter((c) => c > low);
      const varies = Math.max.apply(null, prices) !== low;
      priceLine.replaceChildren(
        el('span', { text: (varies ? T.from + ' ' : '') + money(low) }),
        compares.length
          ? el('s', { class: 'lpc-card-sheet__compare' }, [
              el('span', { class: 'lpc-card__sr', text: T.regularPrice + ' ' }),
              money(Math.min.apply(null, compares)),
            ])
          : null
      );

      detailsLink.setAttribute('href', withVariant(card.dataset.url, (chosen || shown || start).id));

      const soldOut = !product.variants.some((v) => v.available);
      addButton.textContent = soldOut ? T.soldOut : T.addToCart;
      addButton.setAttribute('aria-disabled', String(soldOut));
    };

    form.addEventListener('change', (event) => {
      if (event.target.name === 'lpc-quick-color') state.color = event.target.value;
      if (event.target.name === 'lpc-quick-size') state.size = event.target.value;
      say('');
      refresh();
    });

    let busy = false;
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (busy || addButton.getAttribute('aria-disabled') === 'true') return;

      const variant = pick(state.color, state.size);
      if (!variant) {
        say(T.chooseSize, true);
        const firstSize = form.querySelector('input[name="lpc-quick-size"]:not(:disabled)');
        if (firstSize) firstSize.focus();
        return;
      }
      if (!variant.available) {
        say(T.soldOut, true);
        return;
      }

      busy = true;
      addButton.setAttribute('aria-disabled', 'true');
      addButton.textContent = T.adding;
      say('');

      addToCart(variant.id)
        .then((cart) => {
          s.dialog.close();
          showCart(cart, variant.id, s.opener);
        })
        .catch((error) => {
          busy = false;
          addButton.removeAttribute('aria-disabled');
          addButton.textContent = T.addToCart;
          say(error.message || T.addError, true);
        });
    });

    refresh();
  }

  /* ---------- cart ---------- */

  // True only when the cart app's own script is running; otherwise the paths below are Dawn's, unchanged.
  function supercartActive() {
    return !!(window.lpcCartBubble && window.lpcCartBubble.active());
  }

  function addToCart(id) {
    const base = window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add';
    return fetch(base + '.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        id: id,
        quantity: 1,
        sections: supercartActive() ? 'cart-icon-bubble' : 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json().then((json) => ({ ok: response.ok, json: json })))
      .then((result) => {
        if (!result.ok || result.json.status) {
          throw new Error(result.json.description || result.json.message || T.addError);
        }
        return result.json;
      });
  }

  /* Hand the response to Dawn's cart drawer; fall back to the cart page. */
  function showCart(cart, variantId, opener) {
    const supercart = supercartActive();
    const drawer = document.querySelector('cart-drawer');
    try {
      if (!supercart && (!drawer || !cart.sections)) throw new Error('no cart drawer');
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'lpc-quick-add', productVariantId: variantId, cartData: cart }).catch(
          () => {}
        );
      }
      if (supercart) {
        // SuperCart opens itself on the add: no redirect, no click on the cart icon, just the header count.
        window.lpcCartBubble.paint(cart.sections && cart.sections['cart-icon-bubble']);
        window.lpcCartBubble.expectOpen();
        return;
      }
      if (opener && drawer.setActiveElement) drawer.setActiveElement(opener);
      drawer.renderContents(cart);
      drawer.classList.remove('is-empty'); // Dawn's product form does the same after an add
    } catch (error) {
      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
    }
  }

  /* ---------- wiring ---------- */

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const dot = event.target.closest('[data-lpc-dot]');
    if (dot) {
      pickDot(dot);
      return;
    }

    const more = event.target.closest('[data-lpc-more]');
    if (more) {
      showAllDots(more);
      return;
    }

    const quick = event.target.closest('[data-lpc-quick]');
    const modified = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
    if (quick && !modified && typeof HTMLDialogElement === 'function') {
      event.preventDefault(); // without dialog support the link still opens the product page
      openQuickAdd(quick);
    }
  });

  /* Start loading a colour's picture as soon as the pointer reaches its dot. */
  document.addEventListener('pointerover', (event) => {
    if (!(event.target instanceof Element)) return;
    const dot = event.target.closest('[data-lpc-dot]');
    if (!dot || dot.dataset.preloaded || !dot.dataset.image) return;
    dot.dataset.preloaded = 'true';
    new Image().src = withWidth(dot.dataset.image, 700);
  });
})();
